import type { ComponentType } from 'react';
import { CalendarCheck, CalendarDays, Plus, UserRound } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BrandExpectNotice } from './brand-expect-notice';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useElementHeight } from '../widget-hooks/useElementHeight';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { isMobileLegalFooterPath } from '../widget-lib/mobile-shell-paths';
import { PATH } from '../widget-lib/path-routes';
import { useSafeAreaInsets } from '../widget-lib/safe-area-bridge';
import { BlueHeader } from '../widget-ui/blue-header';
import { EmbedSiteFooter } from '../widget-ui/embed-site-footer';

type TrainerTab = {
  /** Короткая подпись на панели. */
  label: string;
  /** Полное название раздела (контракт меню тренера) — для экранного диктора. */
  title: string;
  path: string;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  primary?: boolean;
};

/**
 * Разделы тренерского приложения (контракт меню: Мои тренировки, Рабочий график, Записать клиента, Профиль).
 * «Записать клиента» — выделенная центральная кнопка, отдельной дублирующей кнопки нет.
 */
const TRAINER_TABS: TrainerTab[] = [
  { label: 'Тренировки', title: 'Мои тренировки', path: PATH.trainerMySessions, icon: CalendarCheck },
  { label: 'График', title: 'Рабочий график', path: PATH.trainerWorkSchedule, icon: CalendarDays },
  { label: 'Записать', title: 'Записать клиента', path: PATH.trainerBookClient, icon: Plus, primary: true },
  { label: 'Профиль', title: 'Профиль', path: PATH.trainerProfile, icon: UserRound }
];

const TRAINER_HEADER_FALLBACK_PX = 48;
const TRAINER_BOTTOM_BAR_FALLBACK_PX = 64;

/** Оболочка тренерского мобильного приложения (`wn=trainer-mobile` / `widget_channel=mobile` на `/trainer…`). */
export function TrainerMobileShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const { accessToken, widgetConfig, guestShowcaseOrg, me } = useEmbedBootstrap();
  const insets = useSafeAreaInsets();
  const header = useElementHeight<HTMLDivElement>(TRAINER_HEADER_FALLBACK_PX);
  const bottomBar = useElementHeight<HTMLElement>(TRAINER_BOTTOM_BAR_FALLBACK_PX);
  const pathname = location.pathname.replace(/\/+$/, '') || '/';

  const activeBranchName =
    me?.trainer_placements?.find((p) => p.branch_id === me.active_branch_id)?.branch_name?.trim() || null;
  const orgName =
    me?.memberships?.find((m) => m.organization_id === me.organization_id)?.organization_name?.trim() || null;
  const title =
    (widgetConfig?.name && widgetConfig.name.trim()) ||
    (guestShowcaseOrg?.name && guestShowcaseOrg.name.trim()) ||
    activeBranchName ||
    orgName ||
    'DomTrainer';

  const hideChrome = pathname === PATH.trainerSignIn || !accessToken;

  function isActive(path: string): boolean {
    return pathname === path || pathname.startsWith(`${path}/`);
  }

  function onTabPress(path: string) {
    if (!accessToken) {
      navigate(`${PATH.trainerSignIn}${suffix}`);
      return;
    }
    navigate(`${path}${suffix}`);
  }

  return (
    <div
      id="domcrm-embed-trainer-mobile"
      className="relative flex min-h-screen w-full max-w-full flex-col overflow-x-hidden bg-[var(--bg)] text-base leading-normal"
      style={{
        paddingTop: hideChrome ? `${insets.top}px` : `${header.height}px`,
        paddingBottom: hideChrome ? `${insets.bottom}px` : `${bottomBar.height}px`
      }}
    >
      {!hideChrome ? (
        <div
          ref={header.ref}
          className="fixed top-0 left-0 right-0 z-40 bg-[var(--accent-bg)] text-[var(--accent-fg)] shadow-[var(--shadow-card)]"
          style={{ paddingTop: `${insets.top}px` }}
        >
          <BlueHeader title={title} />
        </div>
      ) : null}

      <BrandExpectNotice />
      <main className="flex flex-1 flex-col px-4 pb-4 pt-2">
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
        {isMobileLegalFooterPath(pathname) ? <EmbedSiteFooter className="mt-4 shrink-0 pb-1" /> : null}
      </main>

      {!hideChrome ? (
        <nav
          ref={bottomBar.ref}
          className="fixed bottom-0 left-0 right-0 z-40 flex items-stretch border-t border-[var(--border)] bg-[var(--bg-elevated)]"
          style={{ paddingBottom: `${insets.bottom}px` }}
          aria-label="Разделы тренера"
        >
          {TRAINER_TABS.map((tab) => {
            const active = isActive(tab.path);
            const Icon = tab.icon;
            return (
              <button
                key={tab.path}
                type="button"
                onClick={() => onTabPress(tab.path)}
                aria-label={tab.title}
                aria-current={active ? 'page' : undefined}
                className={`flex min-h-[60px] flex-1 flex-col items-center justify-center gap-1 py-2 text-center transition-colors ${
                  active ? 'text-[var(--accent)]' : 'text-[var(--text-muted)] active:text-[var(--text)]'
                }`}
              >
                {tab.primary ? (
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-[var(--accent-fg)] shadow-md ${
                      active
                        ? 'bg-[var(--accent-bg)] ring-2 ring-[color-mix(in_srgb,var(--accent-fg)_45%,transparent)]'
                        : 'bg-[var(--accent-bg)]'
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                ) : (
                  <Icon className="h-6 w-6" aria-hidden />
                )}
                <span className="max-w-full truncate px-0.5 text-[11px] font-medium leading-tight">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
